import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import * as THREE from 'three'
import { Lock } from 'lucide-react'
import { useAppStore } from '@core/store'
import { viewerApi } from '@viewer/lib/viewerApi'
import { getModelsBbox } from './sectionMath'
import { type PlanoDeCorte, planoAVista } from './planosDeCorte'
import {
  type Caixa,
  type P2,
  type V3,
  anguloAVolta,
  baseDoPlano,
  centroide,
  circulo,
  deslocamentoAoLongo,
  poligonoDoPlano,
  poligonoNoEcra,
  pontoNoEcra,
  raioNoPlanoDeRotacao,
  rodar,
} from './desenhoDeCorte'
import { camaraDoViewer } from './camaraDoViewer'

/** Os planos soltos, desenhados no 3D e mexidos à mão.
 *
 *  Cada plano activo aparece como o polígono que corta no volume do
 *  modelo, com três pegas: a do centro desloca-o ao longo da normal, as
 *  outras duas rodam-no em torno do centro. Com Shift, a rotação vai de
 *  5 em 5 graus.
 *
 *  Rodar é como um volante: o rato bate no plano de rotação e o ângulo
 *  é o que ele descreve à volta do centro, com o círculo desenhado
 *  enquanto se arrasta. A conta antiga media o movimento ao longo da
 *  normal no ecrã, e encravava com o plano de frente para a câmara (a
 *  normal vista de topo não tem comprimento no ecrã). Só quando o plano
 *  de rotação fica de perfil se volta a essa conta.
 *
 *  Um plano escondido (`mostrar: false`) não se desenha; um travado
 *  desenha-se a tracejado, sem pegas. */

// Ciano, para não se confundir com a caixa, que usa a cor de destaque.
const COR = '#22d3ee'
const PASSO_DO_ANGULO = (5 * Math.PI) / 180

const raycaster = new THREE.Raycaster()

/** O raio do rato no mundo, a partir das coordenadas do ecrã. */
function raioDoRato(x: number, y: number): { origem: V3; direccao: V3 } | null {
  const wld = viewerApi.getWorld()
  if (!wld) return null
  const r = wld.renderer!.three.domElement.getBoundingClientRect()
  if (r.width === 0 || r.height === 0) return null
  const ndc = new THREE.Vector2(((x - r.left) / r.width) * 2 - 1, -((y - r.top) / r.height) * 2 + 1)
  raycaster.setFromCamera(ndc, wld.camera.three)
  const o = raycaster.ray.origin
  const d = raycaster.ray.direction
  return { origem: [o.x, o.y, o.z], direccao: [d.x, d.y, d.z] }
}

function mais(a: V3, b: V3, k = 1): V3 {
  return [a[0] + b[0] * k, a[1] + b[1] * k, a[2] + b[2] * k]
}
function menos(a: V3, b: V3): V3 {
  return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]
}
function interno(a: V3, b: V3): number {
  return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]
}
function externo(a: V3, b: V3): V3 {
  return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]
}

/** A caixa do modelo, um pouco maior, para o plano se desenhar até às
 *  bordas mesmo quando passa rente a uma fachada. */
function caixaDoModelo(): Caixa | null {
  const b = getModelsBbox()
  if (!b) return null
  const folga = b.min.distanceTo(b.max) * 0.04
  return {
    min: [b.min.x - folga, b.min.y - folga, b.min.z - folga],
    max: [b.max.x + folga, b.max.y + folga, b.max.z + folga],
  }
}

interface Geometria {
  plano: PlanoDeCorte
  poligono: V3[]
  centro: V3
  /** Pegas de inclinação: a direcção sobre o plano e a distância ao centro. */
  pegas: Array<{ direccao: V3; raio: number }>
}

function geometriaDoPlano(plano: PlanoDeCorte, caixa: Caixa): Geometria {
  const poligono = poligonoDoPlano(plano.normal, plano.ponto, caixa)
  // O centro: o do polígono, ou, com o plano fora do modelo, o centro da
  // caixa levado ao plano. Assim a pega nunca desaparece e o plano
  // pode sempre voltar para dentro.
  let centro = centroide(poligono)
  if (!centro) {
    const meio: V3 = [
      (caixa.min[0] + caixa.max[0]) / 2,
      (caixa.min[1] + caixa.max[1]) / 2,
      (caixa.min[2] + caixa.max[2]) / 2,
    ]
    centro = mais(meio, plano.normal, -interno(plano.normal, menos(meio, plano.ponto)))
  }
  const [u, w] = baseDoPlano(plano.normal)
  const alcance = (d: V3) => {
    let m = 0
    for (const p of poligono) m = Math.max(m, Math.abs(interno(menos(p, centro!), d)))
    if (m === 0) m = Math.hypot(caixa.max[0] - caixa.min[0], caixa.max[1] - caixa.min[1], caixa.max[2] - caixa.min[2]) / 6
    return m * 0.7
  }
  return { plano, poligono, centro, pegas: [{ direccao: u, raio: alcance(u) }, { direccao: w, raio: alcance(w) }] }
}

type Arrasto =
  | { tipo: 'mover'; id: string; pointerId: number; inicio: P2; ponto0: V3; normal0: V3; centro: V3 }
  | {
      tipo: 'inclinar'
      id: string
      pointerId: number
      inicio: P2
      normal0: V3
      centro: V3
      pega: V3
      eixo: V3
      raio: number
      /** Onde o rato bateu no plano de rotação ao pegar, a partir do
       *  centro. Null: o plano de rotação estava de perfil, e roda-se
       *  pela conta antiga. */
      v0: V3 | null
    }

interface Guia {
  id: string
  centro: V3
  eixo: V3
  raio: number
}

function PlanosDeCorteOverlay() {
  const planos = useAppStore((s) => s.planosDeCorte)
  const actualizarPlano = useAppStore((s) => s.actualizarPlano)
  const [, forcar] = useState(0)
  const impressaoRef = useRef('')
  const [angulo, setAngulo] = useState<{ id: string; graus: number } | null>(null)
  const [guia, setGuia] = useState<Guia | null>(null)
  const activos = planos.filter((p) => p.activo && planoAVista(p))
  const haPlanos = activos.length > 0

  useEffect(() => {
    if (!haPlanos) return
    let raf = 0
    let parado = false
    const tick = () => {
      if (parado) return
      const world = viewerApi.getWorld()
      if (world) {
        const m = world.camera.three.matrixWorldInverse.elements
        const cnv = world.renderer!.three.domElement
        const fp = `${m.map((x) => x.toFixed(4)).join('|')}|${cnv.clientWidth}x${cnv.clientHeight}`
        if (fp !== impressaoRef.current) {
          impressaoRef.current = fp
          forcar((t) => (t + 1) & 0xffff)
        }
      }
      raf = requestAnimationFrame(tick)
    }
    raf = requestAnimationFrame(tick)
    return () => {
      parado = true
      cancelAnimationFrame(raf)
    }
  }, [haPlanos])

  if (!haPlanos) return null
  const world = viewerApi.getWorld()
  const cam = camaraDoViewer()
  const caixa = caixaDoModelo()
  if (!world || !cam || !caixa) return null

  const geometrias = activos.map((p) => geometriaDoPlano(p, caixa))

  const comecar = (evento: ReactPointerEvent<HTMLButtonElement>, arrasto: Arrasto) => {
    evento.preventDefault()
    evento.stopPropagation()
    const wld = viewerApi.getWorld()
    if (!wld) return
    const cnv = wld.renderer!.three.domElement
    const rect = cnv.getBoundingClientRect()
    arrasto.inicio = { x: evento.clientX - rect.left, y: evento.clientY - rect.top }
    const pointerId = arrasto.pointerId
    wld.camera.controls.enabled = false
    if (arrasto.tipo === 'inclinar') {
      const r0 = raioDoRato(evento.clientX, evento.clientY)
      const hit = r0 && raioNoPlanoDeRotacao(r0.origem, r0.direccao, arrasto.centro, arrasto.eixo)
      arrasto.v0 = hit ? menos(hit, arrasto.centro) : null
      setGuia({ id: arrasto.id, centro: arrasto.centro, eixo: arrasto.eixo, raio: arrasto.raio })
    }

    const mexer = (e: globalThis.PointerEvent) => {
      if (e.pointerId !== pointerId) return
      const c = camaraDoViewer()
      if (!c) return
      const r = cnv.getBoundingClientRect()
      const rato = { x: e.clientX - r.left, y: e.clientY - r.top }
      if (arrasto.tipo === 'mover') {
        const t = deslocamentoAoLongo(c, arrasto.centro, arrasto.normal0, rato, arrasto.inicio)
        if (t === null) return
        actualizarPlano(arrasto.id, { ponto: mais(arrasto.ponto0, arrasto.normal0, t) })
        return
      }
      // Rodar: o ângulo que o rato descreveu à volta do centro, no plano
      // de rotação. Roda-se a normal em torno do eixo, e o plano
      // continua a passar no centro.
      let ang: number
      if (arrasto.v0) {
        const r1 = raioDoRato(e.clientX, e.clientY)
        const hit = r1 && raioNoPlanoDeRotacao(r1.origem, r1.direccao, arrasto.centro, arrasto.eixo)
        // De perfil por um instante: fica onde está até o rato voltar.
        if (!hit) return
        ang = anguloAVolta(arrasto.v0, menos(hit, arrasto.centro), arrasto.eixo)
      } else {
        // Plano de rotação de perfil: quanto a pega andou ao longo da
        // normal, visto do centro.
        const t = deslocamentoAoLongo(c, arrasto.pega, arrasto.normal0, rato, arrasto.inicio)
        if (t === null) return
        ang = Math.atan2(t, arrasto.raio)
      }
      if (e.shiftKey) ang = Math.round(ang / PASSO_DO_ANGULO) * PASSO_DO_ANGULO
      const normal = rodar(arrasto.normal0, arrasto.eixo, ang)
      actualizarPlano(arrasto.id, { normal, ponto: arrasto.centro })
      setAngulo({ id: arrasto.id, graus: (ang * 180) / Math.PI })
    }
    const largar = (e: globalThis.PointerEvent) => {
      if (e.pointerId !== pointerId) return
      const wl = viewerApi.getWorld()
      if (wl) wl.camera.controls.enabled = true
      setAngulo(null)
      setGuia(null)
      document.removeEventListener('pointermove', mexer)
      document.removeEventListener('pointerup', largar)
      document.removeEventListener('pointercancel', largar)
    }
    document.addEventListener('pointermove', mexer)
    document.addEventListener('pointerup', largar)
    document.addEventListener('pointercancel', largar)
  }

  const estiloDaPega =
    'pointer-events-auto absolute flex items-center justify-center rounded-full border-2 bg-[var(--color-surface)]/95 shadow-lg'

  return (
    <div className="pointer-events-none absolute inset-0 z-10 overflow-hidden">
      <svg className="absolute inset-0 h-full w-full" width={cam.largura} height={cam.altura}>
        {geometrias.map((g) => {
          const pts = poligonoNoEcra(cam, g.poligono)
          if (pts.length < 3) return null
          return (
            <polygon
              key={g.plano.id}
              points={pts.map((q) => `${q.x},${q.y}`).join(' ')}
              fill={COR}
              fillOpacity={g.plano.travado ? 0.04 : 0.1}
              stroke={COR}
              strokeWidth={2}
              strokeOpacity={g.plano.travado ? 0.6 : 0.9}
              strokeDasharray={g.plano.travado ? '8 5' : undefined}
            />
          )
        })}
        {guia &&
          (() => {
            const pts = poligonoNoEcra(cam, circulo(guia.centro, guia.eixo, guia.raio))
            if (pts.length < 3) return null
            return (
              <polygon
                points={pts.map((q) => `${q.x},${q.y}`).join(' ')}
                fill="none"
                stroke={COR}
                strokeWidth={1.5}
                strokeDasharray="5 4"
                strokeOpacity={0.9}
              />
            )
          })()}
        {geometrias.map((g) =>
          g.plano.travado ? null : g.pegas.map((pg, i) => {
            const a = pontoNoEcra(cam, g.centro)
            const b = pontoNoEcra(cam, mais(g.centro, pg.direccao, pg.raio))
            if (!a || !b) return null
            return (
              <line
                key={`${g.plano.id}-l${i}`}
                x1={a.x}
                y1={a.y}
                x2={b.x}
                y2={b.y}
                stroke={COR}
                strokeWidth={1.5}
                strokeDasharray="4 3"
                strokeOpacity={0.8}
              />
            )
          }),
        )}
      </svg>
      {geometrias.map((g) => {
        const c = pontoNoEcra(cam, g.centro)
        if (!c) return null
        // A seta aponta para o lado que fica, como a normal.
        const ponta = pontoNoEcra(cam, mais(g.centro, g.plano.normal, 1))
        const graus = ponta ? (Math.atan2(ponta.y - c.y, ponta.x - c.x) * 180) / Math.PI : 0
        const inclinacao = angulo?.id === g.plano.id ? angulo.graus : null
        if (g.plano.travado) {
          return (
            <div
              key={g.plano.id}
              className="pointer-events-none absolute flex items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium shadow"
              style={{ left: c.x + 4, top: c.y - 12, background: COR, color: '#042f35' }}
              title={`${g.plano.nome} (travado)`}
            >
              <Lock size={10} aria-hidden />
              {g.plano.nome}
            </div>
          )
        }
        return (
          <div key={g.plano.id}>
            <button
              type="button"
              className={`${estiloDaPega} h-9 w-9`}
              style={{
                left: c.x,
                top: c.y,
                borderColor: COR,
                color: COR,
                transform: `translate(-50%, -50%) rotate(${graus}deg)`,
                cursor: 'grab',
              }}
              title={`${g.plano.nome}: arrastar para deslocar o plano ao longo da normal`}
              onPointerDown={(e) =>
                comecar(e, {
                  tipo: 'mover',
                  id: g.plano.id,
                  pointerId: e.pointerId,
                  inicio: { x: 0, y: 0 },
                  ponto0: g.plano.ponto,
                  normal0: g.plano.normal,
                  centro: g.centro,
                })
              }
            >
              <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
                <path
                  d="M2 8 H13 M9.5 4.5 L13 8 L9.5 11.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <div
              className="pointer-events-none absolute whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium shadow"
              style={{ left: c.x + 22, top: c.y - 28, background: COR, color: '#042f35' }}
            >
              {g.plano.nome}
              {inclinacao !== null && ` · ${inclinacao.toFixed(1).replace('.', ',')}°`}
            </div>
            {g.pegas.map((pg, i) => {
              const ponto = mais(g.centro, pg.direccao, pg.raio)
              const p = pontoNoEcra(cam, ponto)
              if (!p) return null
              return (
                <button
                  key={i}
                  type="button"
                  className={`${estiloDaPega} h-7 w-7`}
                  style={{
                    left: p.x,
                    top: p.y,
                    borderColor: COR,
                    color: COR,
                    transform: 'translate(-50%, -50%)',
                    cursor: 'grab',
                  }}
                  title={`${g.plano.nome}: arrastar em círculo para rodar o plano à volta do centro (Shift: de 5 em 5 graus)`}
                  onPointerDown={(e) =>
                    comecar(e, {
                      tipo: 'inclinar',
                      id: g.plano.id,
                      pointerId: e.pointerId,
                      inicio: { x: 0, y: 0 },
                      normal0: g.plano.normal,
                      centro: g.centro,
                      pega: ponto,
                      // Rodar a direcção da pega em torno de (pega x normal)
                      // leva-a para o lado da normal: o plano inclina
                      // para onde se puxa.
                      eixo: externo(pg.direccao, g.plano.normal),
                      raio: pg.raio,
                      v0: null,
                    })
                  }
                >
                  <svg viewBox="0 0 16 16" width="14" height="14" aria-hidden>
                    <path
                      d="M3 10 A5.5 5.5 0 0 1 13 10 M13 10 L13 6 M13 10 L9.2 9.4"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={1.8}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              )
            })}
          </div>
        )
      })}
    </div>
  )
}

export default PlanosDeCorteOverlay
